import Vue from "vue";
import VueRouter from "vue-router";
import routes from "./routes";
import store from "../store";
import { rememberRouteComponent } from "@/utils/keepAlive";
import { setLiveComponent } from "@/utils/taskbarLive";
import { routerScrollBehavior } from "@/utils/routerScroll";
import { dropSidebarResetsExcept } from "@/utils/sidebarReset";

Vue.use(VueRouter);

// configure router
const router = new VueRouter({
    mode: "history",
    routes, // short for routes: routes
    linkActiveClass: "active",
    // Query-only navigation keeps the scroll position (spec 2026-10-07 §11.1).
    scrollBehavior: routerScrollBehavior
});

router.beforeEach((to, from, next) => {
    if (to.matched.some(record => record.meta.requiresAuth)) 
	{
        if (!store.getters["Auth/isAuthenticate"]) 
		{
            next({
                path: "/login",
                params: { nextUrl: to.fullPath }
            });
        } else 
		{
		if (to.matched.some(record => record.meta.requiresOrderReturn) && !store.getters["Auth/isAccessOrderReturn"]){
				next({
					path: "/backoffice/orders",
					params: { nextUrl: to.fullPath }
				});
				return;
			}
			if (to.matched.some(record => record.meta.requiresDashboardByPlatformChannel) && !store.getters["Auth/isAccessDashboardByPlatformChannel"]){
				next({
					path: "/backoffice/reports/dashboard",
					params: { nextUrl: to.fullPath }
				});
				return;
			}
			if (to.matched.some(record => record.meta.requiresAgentCommission) && !store.getters["Auth/isAccessAgentCommission"]){
				next({
					path: "/backoffice/orders",
					params: { nextUrl: to.fullPath }
				});
				return;
			}
			// ต้นทุน is an email allowlist in the API's config.yml, not a role
			// (bo-api #594 §4.1). The server refuses with 403 regardless; this
			// guard only stops the screen from opening empty and swal-ing.
			if (to.matched.some(record => record.meta.requiresProductCost) && !store.getters["Auth/canViewCost"]){
				next({
					path: "/backoffice/orders",
					params: { nextUrl: to.fullPath }
				});
				return;
			}
			if (to.matched.some(record => record.meta.requiresSeller) && !store.getters["Auth/isSeller"]){
				next({
					path: "/backoffice/orders",
					params: { nextUrl: to.fullPath }
				});
				return;
			}
			if (to.matched.some(record => record.meta.requiresModernTrade) && !store.getters["Auth/isAccessModernTrade"]){
				next({
					path: "/backoffice/orders",
					params: { nextUrl: to.fullPath }
				});
				return;
			}
			// POS (§13.1): every BO POS page needs roles.pos_bo; devices also level 2,
			// events / products / settings level 4.
			if (to.matched.some(record => record.meta.requiresPosBO) && !store.getters["Auth/isPosBO"]){
				next({
					path: "/backoffice/orders",
					params: { nextUrl: to.fullPath }
				});
				return;
			}
			if (to.matched.some(record => record.meta.requiresPosBOSupervisor) && !store.getters["Auth/isPosBOSupervisor"]){
				next({
					path: "/backoffice/orders",
					params: { nextUrl: to.fullPath }
				});
				return;
			}
			if (to.matched.some(record => record.meta.requiresPosBOAdmin) && !store.getters["Auth/isPosBOAdmin"]){
				next({
					path: "/backoffice/orders",
					params: { nextUrl: to.fullPath }
				});
				return;
			}
			// (pre-§13 flags, no POS route uses them any more)
			if (to.matched.some(record => record.meta.requiresPosAdmin) && !store.getters["Auth/isPosAdmin"]){
				next({
					path: "/backoffice/orders",
					params: { nextUrl: to.fullPath }
				});
				return;
			}
			if (to.matched.some(record => record.meta.requiresPosSupervisor) && !store.getters["Auth/isPosSupervisor"]){
				next({
					path: "/backoffice/orders",
					params: { nextUrl: to.fullPath }
				});
				return;
			}
			// /backoffice/orders-overview: MadameFin staff only (#441). Agents
			// bounce to their own order list.
			if (to.matched.some(record => record.meta.requiresMdfStaff) && !store.getters["Auth/isMdfStaff"]){
				next({ path: "/backoffice/orders" });
				return;
			}
			// ...and only users holding the "ภาพรวมรายการสั่งซื้อ" right (order_overview)
			if (to.matched.some(record => record.meta.requiresOrderOverview) && !store.getters["Auth/isAccessOrderOverview"]){
				next({ path: "/backoffice/orders" });
				return;
			}
			// การตลาด (Phase 1 ads §6.1): roles.marketing (read) for the LP pages + source report
			if (to.matched.some(record => record.meta.requiresMarketing) && !store.getters["Auth/isAccessMarketing"]){
				next({ path: "/backoffice/orders" });
				return;
			}
			// Phase 3 affiliate §5.1: finance-only pages (payout rounds) and the
			// pages both roles share (members, ledger, report).
			if (to.matched.some(record => record.meta.requiresAffiliateFinance) && !store.getters["Auth/isAccessAffiliateFinance"]){
				next({ path: "/backoffice/orders" });
				return;
			}
			if (to.matched.some(record => record.meta.requiresMarketingOrAffiliateFinance) &&
				!store.getters["Auth/isAccessMarketing"] && !store.getters["Auth/isAccessAffiliateFinance"]){
				next({ path: "/backoffice/orders" });
				return;
			}
			if (to.matched.some(record => record.meta.requiresAdmin)) {
				const isAdmin = store.getters["Auth/isAdmin"];
				if (!isAdmin) {
					next({ path: "/backoffice/orders" });
					return;
				}
			}
			next();
        }
    } else if (to.matched.some(record => record.meta.guest)) 
	{
        next();
    } else 
	{
        next();
    }
});

// Per-tab keep-alive (docs/specs/2026-10-06-bo-taskbar-fill-keepalive-design.md
// §3.2). beforeResolve runs after async route components are resolved and
// before the route commits, so the page's component name is in
// DashboardLayout's keep-alive `include` before its first render (B6) — an
// anonymous page is named here, before Vue.extend ever sees it.
router.beforeResolve((to, from, next) => {
    rememberRouteComponent(to, setLiveComponent);
    next();
});

// Sidebar "start over" flags (src/utils/sidebarReset.js, #478): a completed
// navigation drops every flag it did not target, so none can linger.
router.afterEach((to) => {
    dropSidebarResetsExcept(to);
});

export default router;
